import type {
  CartItemOptions,
  CartItemPayload,
  CartUpdateItemReqBody,
} from '@sola/schema/dotcom/cart/api.ts';
import type {
  CartItem,
  CartItemRelEnum,
} from '@sola/schema/dotcom/cart/cart-item.ts';
import { CartItemTypeEnum } from '@sola/schema/dotcom/cart/cart-item.ts';
import type {
  Cart,
  CartUpsellActionEnum,
} from '@sola/schema/dotcom/cart/cart.ts';
import type { CartItemPaymentPayload } from '@sola/schema/dotcom/cart/payment.ts';
import type { UpsellItem } from '@sola/schema/dotcom/checkout/upsell.ts';
import { UpsellItemTypeEnum } from '@sola/schema/dotcom/checkout/upsell.ts';
import {
  CartItemTypeCodeEnum,
  LEGACY_ANY_PRODUCT_VALUE,
  UpsellTypeEnum,
} from '@sola/schema/dotcom/shared/content/campaigns.ts';
import {
  sendCartAddEvent,
  sendCartRemoveEvent,
} from '@sola/shared-utils/analytics-events.ts';
import {
  isItemFund,
  isItemSponsorship,
} from '@sola/shared-utils/cart/cart-item-type-guards.ts';

import {
  addItemsRequest,
  getCartRequest,
  getRestoredCartRequest,
  logoutRequest,
  removeItemRequest,
  updateItemRequest,
  updatePaymentItemsRequest,
  updateUpsellRequest,
} from './requests.ts';
import {
  getUpsellFundItem,
  getUpsellSponsorshipItem,
} from './utils/upsells.ts';

/**
 * Format cart items based on their item types.
 */
const formatItems = (
  items: CartItemPayload[],
  options?: CartItemOptions,
): CartItemPayload[] => {
  return items.map((item): CartItemPayload => {
    switch (item.itemType) {
      case CartItemTypeEnum.Fund:
      case CartItemTypeEnum.Gift: {
        return {
          ...item,
          ...options,
        };
      }
      default: {
        return item;
      }
    }
  });
};

/**
 * Order items
 */
export const sortItems = (cartItems: CartItem[]) => {
  return cartItems.sort((item1, item2) => {
    // Although not necessary, getTime() plays better with TS
    return new Date(item2.added).getTime() - new Date(item1.added).getTime();
    // return item1.itemType === CartItemTypeEnum.Sponsorship ? -1 : 0
  });
};

/**
 * Check for and retrieve current cart.
 */
export const fetchCart = async (
  queryString?: Record<string, string>,
): Promise<Cart | null | undefined> => {
  // See if we have a cart in session.
  // const localCart = getCartFromStorage();
  // if (localCart) {
  // 	return localCart;
  // }

  // TODO: Remove this commented code. Just documenting that our SDKs (client-side) don't have access to the cartId as we make the cookie HttpOnly.
  //		 However, we don't need the application to read the cookie - requests to the cart API will include the cookie
  // const existingCartId = getPersistedCartID();
  // if (!existingCartId) {
  // 	return null;
  // }

  const cart = await getCartRequest(undefined, queryString);
  if (!cart) {
    return null;
  }

  // saveCartSession();

  // TODO: Remove this commented code. We don't want the SDK writing the cart cookie - only the API
  // Make sure to add it to the current session.
  // if (cart) {
  // 	updateCartStorage(cart);
  // }

  return cart;
};

/*
 * Add item(s)
 */
export const addToCart = async ({
  items,
  options,
  supporterId,
  referrer,
}: {
  items: CartItemPayload | CartItemPayload[];
  options?: CartItemOptions;
  supporterId?: string;
  referrer?: string;
}): Promise<Cart | undefined> => {
  const itemsArray = Array.isArray(items) ? items : [items];
  const formattedItems = formatItems(itemsArray, options);
  const updatedCart = await addItemsRequest(
    formattedItems,
    supporterId,
    referrer,
  );

  // saveCartSession();

  const updatedItems = updatedCart?.items.filter(
    (item) => item.added === updatedCart.version,
  );

  if (updatedCart && updatedItems) {
    sendCartAddEvent(updatedItems, updatedCart.id);
  }

  return updatedCart;
};

/**
 * Update an item already in cart.
 */
export const updateItem = async (
  id: CartItem['id'],
  updates: CartUpdateItemReqBody,
): Promise<Cart | void> => {
  // TODO: Guessing we'll need some better handling of errors here
  return await updateItemRequest(id, updates);
};

/**
 * Update payment items in the cart in a single request.
 */
export const updatePaymentItems = async ({
  cartId,
  paymentItems,
}: {
  cartId?: Cart['id'];
  paymentItems: CartItemPaymentPayload[];
}): Promise<Cart | void> => {
  const updatedCart = await updatePaymentItemsRequest(paymentItems, cartId);

  if (updatedCart?.removedItems) {
    sendCartRemoveEvent(updatedCart.removedItems);
  }

  const addedItems = updatedCart?.items.filter(
    (item) => item.added === updatedCart.version,
  );

  if (updatedCart && addedItems) {
    sendCartAddEvent(addedItems, updatedCart.id);
  }

  return updatedCart;
};

/**
 * Remove item(s) based on id.
 */
export const removeFromCart = async (
  id: CartItem['id'],
): Promise<Cart | void> => {
  // TODO: Guessing we'll need some better handling of errors here
  const updatedCart = await removeItemRequest(id);

  if (updatedCart?.removedItems) {
    sendCartRemoveEvent(updatedCart.removedItems);
  }

  return updatedCart;
};

/**
 * Remove item(s) based on id.
 */
export const removeFromCartId = async (
  cartId: Cart['id'],
  itemId: CartItem['id'],
): Promise<Cart | void> => {
  // TODO: Guessing we'll need some better handling of errors here
  const updatedCart = await removeItemRequest(itemId, cartId);

  return updatedCart;
};

/**
 * Deletes items that require authentication from existing cart
 */
export const logOut = async (): Promise<Cart | undefined> => {
  // TODO: Guessing we'll need some better handling of errors here
  return await logoutRequest();
};

/**
 * Get a cart upsell item based on the campaign's upsell override or the default behavior.
 */
export const getUpsell = async (
  itemType: UpsellItemTypeEnum,
  cart?: Cart | null,
): Promise<{
  title?: string;
  subtitle?: string;
  upsellItem: UpsellItem | null | undefined;
  lastItem?: CartItem;
}> => {
  if (!cart || !cart.items || cart.items.length === 0) {
    return { upsellItem: null };
  }

  const upsellOverride = cart.campaign?.upsellOverride;

  /**
   * Don't present upsells if there are any sponsorships already in the cart and there is no upsell override
   */
  if (cart.items.some(isItemSponsorship) && !upsellOverride) {
    return { upsellItem: null };
  }

  const lastItem = cart.items.reduce((laterItem, item) => {
    return item.added > laterItem.added ? item : laterItem;
  }, cart.items[0]);

  const isLastItemFund = isItemFund(lastItem);
  if (upsellOverride?.type === UpsellTypeEnum.CDSP && !isLastItemFund) {
    return { upsellItem: null };
  }

  // Check criterion cart item type from the override, or fall back to Fund-only
  const requiredItemType = upsellOverride?.criterionCartItemType;
  let upsellHasMatchingCartItem = false;
  if (requiredItemType) {
    // Map campaign criterion types to cart item types
    const reqLower = requiredItemType.toLowerCase();
    upsellHasMatchingCartItem =
      reqLower === CartItemTypeCodeEnum.Anything.toLowerCase() ||
      reqLower === CartItemTypeCodeEnum.AnyProduct.toLowerCase() ||
      requiredItemType === LEGACY_ANY_PRODUCT_VALUE ||
      (reqLower === CartItemTypeCodeEnum.CDSP.toLowerCase() &&
        lastItem.itemType === CartItemTypeEnum.Sponsorship) ||
      (reqLower === CartItemTypeCodeEnum.Fund.toLowerCase() &&
        lastItem.itemType === CartItemTypeEnum.Fund);
  } else if (!isItemFund(lastItem)) {
    return { upsellItem: null };
  }

  if (
    !cart.upsell ||
    !cart.upsell.canOffer ||
    cart.upsell.declinedIds.includes(lastItem.id)
  ) {
    return { upsellItem: null };
  }

  // If the campaign has an upsell override, use it to determine what to offer
  if (upsellOverride && upsellHasMatchingCartItem) {
    if (upsellOverride.type === 'Fund' && upsellOverride.fund) {
      return {
        title: upsellOverride.title || undefined,
        subtitle: upsellOverride.subtitle || undefined,
        upsellItem: getUpsellFundItem(upsellOverride.fund),
        lastItem,
      };
    }

    if (upsellOverride.type === 'CDSP') {
      return {
        title: upsellOverride.title || undefined,
        subtitle: upsellOverride.subtitle || undefined,
        upsellItem: await getUpsellSponsorshipItem(upsellOverride.sacFilter),
        lastItem,
      };
    }
  }

  // Default: fetch a sponsorship upsell
  switch (itemType) {
    case UpsellItemTypeEnum.Sponsorship: {
      if (cart.items.some(isItemSponsorship)) {
        return { upsellItem: null };
      }

      return { upsellItem: await getUpsellSponsorshipItem(), lastItem };
    }
    case UpsellItemTypeEnum.Fund: {
      // Fund upsells require an override configuration
      return { upsellItem: null };
    }
  }
};

/**
 * @description Increment the cart upsell accepted count
 */
export const updateUpsell = async ({
  action,
  itemId,
}: {
  action: CartUpsellActionEnum;
  itemId?: string;
}): Promise<Cart | void> => {
  return updateUpsellRequest({ action, itemId });
};

/**
 * Get the related item by relationship.
 */
export const getRelatedItem = (
  parent: CartItem,
  rel: CartItemRelEnum,
  cart: Cart | null,
): CartItem | undefined => {
  if (!parent || !cart) {
    return undefined;
  }

  return cart.items?.find((item) => {
    return item.rel === rel && item.parentId === parent.id;
  });
};

/**
 * Restore cart (including merge with current cart)
 */
export const fetchRestoredCart = async (
  restoreCartId: string,
): Promise<Cart | null | undefined> => {
  const cart = await getRestoredCartRequest(restoreCartId);
  // saveCartSession();

  return cart;
};

/**
 * Check to determine if item is in error
 */
export const isItemInError = (item: CartItem, cart: Cart | null): boolean => {
  if (!cart?.errors?.length) {
    return false;
  }

  return cart.errors.some((err) => err.id === item.id);
};
